import React, { useState, useEffect, useCallback } from "react";
import { Col, Row, Pagination, Collapse, Button, Modal, message } from "antd";
import axios from "axios";
import { CaretRightOutlined, DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";

import PageHeader from "../Layout/PageHeader";
import BeneficiaryList from "./BeneficiaryList";
import BeneficiaryFilters from "./BeneficiaryFilters";

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";


import DragUpload from "../Form/DragUpload";

import RemittanceButton from "../Remittance/RemittanceButton";
const Beneficiary = () => {
    const [loading, setLoading] = useState({ index: false });
    const [isDownloading, setIsDownloading] = useState(false);
    const [payouts, setPayouts] = useState();
    const [filters, setFilters] = useState({});
    const [pagination, setPagination] = useState({ currentPage: 1, limit: 50 });
    const [selectedRows, setSelectedRows] = useState([]);

    const { permissions } = usePermissions();

    const [bulkUpdateMerchant, setBulkUpdateMerchant] = useState();

    const fetchPayouts = useCallback(async () => {
        setLoading((prevLoading) => ({ ...prevLoading, index: true }));

        const params = {
            limit: pagination.limit,
            page: pagination.currentPage,
            ...(filters.search && {
                searchField: filters.searchField,
                search: filters.search,
            }),
            ...(filters.isSuccess !== null && { isSuccess: filters.isSuccess }),
            startDate: filters.startDate?.startOf("day")?.toISOString(),
            endDate: filters.endDate?.endOf("day")?.toISOString(),
            ...(filters.merchantId && {
                merchantId: filters.merchantId
            }),
            ...(filters.status !== null && { status: filters.status })
        };
        const res = await axios.get("/api/payout/beneficiaries", { params });

        setPayouts(res.data.data || []);
        setPagination(res.data.pagination);
        setLoading((prevLoading) => ({ ...prevLoading, index: false }));
    }, [pagination.currentPage, pagination.limit, filters]);

    const downloadCSV = async () => {
        setIsDownloading(true);

        const hasSelection = selectedRows?.length > 0
        const count = hasSelection ? selectedRows.length : pagination?.totalSize

        const messageContent = hasSelection ? (
            <p>
                Are you sure you want to download the {count} selected beneficiaries only?<br />
                To download all {pagination?.totalSize} beneficiaries matching the current filters, clear the selection first.
            </p>
        ) : (
            <p>
                Are you sure you want to download all {count} beneficiaries matching the current filters?<br />
            </p>
        );

        const params = {
            ...(filters.search && {
                searchField: filters.searchField,
                search: filters.search,
            }),
            startDate: filters.startDate?.startOf("day")?.toISOString(),
            endDate: filters.endDate?.endOf("day")?.toISOString(),
            ...(filters.status !== null && { status: filters.status }),
            ...(hasSelection && { "userIds[]": selectedRows })
        };

        Modal.confirm({
            content: messageContent,
            title: `Download Beneficiary List ? `,
            async onOk() {
                await axios
                    .get("/api/payout/beneficiaries/csv", { params })
                    .then((res) => {
                        message.info(`Please check the Export progress in System -> Jobs`);
                    })
                    .catch((error) => {
                        message.error(error.message);
                    });
            },
        });

        setIsDownloading(false);
    };

    const uploadSuccessHandler = (res) => {

        setBulkUpdateMerchant(res.data.bulkUpdateMerchant);

    };

    useEffect(() => {
        fetchPayouts();
    }, [pagination.currentPage, pagination.limit, fetchPayouts]);

    return (
        <div>
            <PageHeader
                title="Beneficiary"
                childRoutes={[{ path: "/beneficiary", breadcrumbName: "Payout" }]}
            />

            {permissions?.includes(PERMISSIONS.UPDATE_PAYMENT_GATEWAY) && (
                <Collapse
                    expandIcon={({ isActive }) => (
                        <CaretRightOutlined rotate={isActive ? 90 : 0} />
                    )}
                >
                    <Collapse.Panel header="Bulk Update Payout Payment Gateway" key="bulkUpdateMerchant">
                        {bulkUpdateMerchant && (
                            <Collapse
                                className="mt-2"
                                size="small"
                                expandIcon={({ isActive }) => (
                                    <CaretRightOutlined rotate={isActive ? 90 : 0} />
                                )}
                            >
                                <Collapse.Panel
                                    header={
                                        <span>
                                            <InfoCircleOutlined /> Results of Last Bulk Update
                                        </span>
                                    }
                                    key="last-bulk-result"
                                >
                                    <h4>
                                        <strong>Success</strong>
                                    </h4>
                                    <ol>
                                        {bulkUpdateMerchant
                                            .filter((result) => result.success)
                                            .map((success) => (
                                                <li key={success.email}>
                                                    {success.email}: {success.paymentProvider}
                                                </li>
                                            ))}
                                    </ol>
                                    <h4 style={{ color: "red" }}>
                                        <strong>Failed</strong>
                                    </h4>
                                    <ol>
                                        {bulkUpdateMerchant
                                            .filter((result) => !result.success)
                                            .map((fail) => (
                                                <li key={fail.email}>
                                                    {fail.email}: {fail.reason} ( row {fail.row} )
                                                </li>
                                            ))}
                                    </ol>
                                </Collapse.Panel>
                            </Collapse>
                        )}

                        <DragUpload
                            action={`${process.env.REACT_APP_SERVER_URL}/api/payout/beneficiaries/updatePayoutProvider`}
                            accept=".xls, .xlsx"
                            method="post"
                            onUploadSuccess={(res) => uploadSuccessHandler(res)}
                            additionalDesc={`Excel file with headers “Payout Merchant Email” and “Payout Payment Provider”. Example: Payout Payment Provider values can be "paydibs" or "commercePay".`}
                        />
                        <br />
                    </Collapse.Panel>
                </Collapse>
            )}

            <Collapse
                expandIcon={({ isActive }) => (
                    <CaretRightOutlined rotate={isActive ? 90 : 0} />
                )}
            >
                <Collapse.Panel header="Filters" key="filters">
                    <BeneficiaryFilters
                        filters={filters}
                        onFiltersChange={(filters) => setFilters(filters)}
                    />
                </Collapse.Panel>
            </Collapse>



            <Row className="mt-2 mb-2" justify="space-between">
                <div>
                    <RemittanceButton selectedRowKeys={selectedRows} filters={filters} />
                    <Button onClick={downloadCSV} loading={isDownloading}>
                        {selectedRows?.length > 0
                            ? `Download CSV (${selectedRows.length} Selected)`
                            : "Download CSV (All)"}{" "}
                        <DownloadOutlined />
                    </Button>
                </div>
                <Col>
                    <Pagination
                        total={pagination?.totalSize}
                        pageSize={pagination?.limit}
                        onChange={(currentPage, limit) => {
                            setPagination({ ...pagination, limit, currentPage });
                        }}
                        current={pagination.currentPage}
                    />
                </Col>
            </Row>

            <BeneficiaryList
                onSelectRows={(selectedRowKeys) => setSelectedRows(selectedRowKeys)}
                selectedRows={selectedRows}
                payouts={payouts}
                loading={loading.index}
                filters={setFilters}
            />

            <Pagination
                total={pagination?.totalSize}
                pageSize={pagination?.limit}
                onChange={(currentPage, limit) => {
                    setPagination({ ...pagination, limit, currentPage });
                }}
                current={pagination.currentPage}
            />
        </div>
    );
};

export default Beneficiary;
